import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Switch, FormControlLabel, Divider, Alert,
  CircularProgress, Chip, Stack, Avatar,
} from '@mui/material';
import { Tune as TuneIcon, BakeryDining as BakeryIcon, Public as XbrlIcon, WaterfallChart as CashFlowIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../../../config';
import { toast } from 'react-toastify';
import { useLanguage } from '../../../contexts/LanguageContext';
import XbrlGeneratorPanel from './XbrlGeneratorPanel';
import XbrlReviewPanel from './XbrlReviewPanel';

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

// سويتشات XBRL — كل سويتش يغيّر سلوكاً اختيارياً، مغلق افتراضياً، للسوبر أدمن فقط.
interface SwitchDef {
  key: string;
  titleAr: string; titleEn: string;
  descAr: string; descEn: string;
  icon: React.ReactNode;
}

const SWITCHES: SwitchDef[] = [
  {
    key: 'XbrlBakeryEliminationEnabled',
    titleAr: 'استبعاد مبيعات المخبز الداخلية',
    titleEn: 'Eliminate bakery internal sales',
    descAr: 'مغلق: المخبز يظهر إيراداً (تقيس ربح المصنع). مفعّل: يُستبعد من الإيراد والتكلفة في التقرير الموحّد الرسمي فقط. لا يمسّ أي قيد أو بيان.',
    descEn: 'OFF: bakery shows as revenue (measure factory profit). ON: excluded from revenue & COGS in the consolidated official report only. Touches no entry or data.',
    icon: <BakeryIcon color="warning" />,
  },
  {
    key: 'XbrlCashFlowImprovedEnabled',
    titleAr: 'قائمة التدفقات النقدية المحسّنة',
    titleEn: 'Improved Cash Flow statement',
    descAr: 'مغلق: التدفقات كما هي حالياً. مفعّل: تُفعَّل إعدادات الحسابات متعددة القيم، وتصحيح المخصصات والأصول غير الملموسة، وسطر «حركات غير مصنّفة» يجعل القائمة تتوازن (رصيد أول + صافي التغير = رصيد آخر).',
    descEn: 'OFF: cash flow as it is now. ON: multi-account CF settings become active, provisions/intangibles corrected, and an "Unclassified movements" line makes the statement reconcile (Beginning + Net change = Ending).',
    icon: <CashFlowIcon color="info" />,
  },
];

const XbrlSettingsTab: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const isSuperAdmin = (() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw)?.isSuperAdmin === true : false;
    } catch { return false; }
  })();

  const [values, setValues] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const next: Record<string, boolean> = {};
      for (const s of SWITCHES) {
        try {
          const res = await axios.get(`${config.API_BASE_URL}/SystemSettings/${s.key}`, authHeader());
          next[s.key] = String(res.data?.value).toLowerCase() === 'true';
        } catch { next[s.key] = false; }
      }
      setValues(next);
      setLoading(false);
    };
    load();
  }, []);

  const toggle = async (key: string, on: boolean) => {
    setSaving(key);
    try {
      await axios.put(`${config.API_BASE_URL}/SystemSettings/${key}`, { value: on ? 'true' : 'false' }, authHeader());
      setValues(prev => ({ ...prev, [key]: on }));
      toast.success(isRTL ? 'تم الحفظ' : 'Saved');
    } catch {
      toast.error(isRTL ? 'فشل الحفظ' : 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {isRTL ? 'هذه الإعدادات متاحة للسوبر أدمن فقط.' : 'These settings are available to Super Admin only.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Gradient header band */}
      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, boxShadow: '0 8px 32px rgba(102,126,234,0.25)' }}>
        <Box sx={{ background: GRADIENT, color: '#fff', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
            <XbrlIcon sx={{ color: '#fff' }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {isRTL ? 'سويتشات الوسم (XBRL)' : 'XBRL Switches'}
              </Typography>
              <Chip size="small" label={isRTL ? 'سوبر أدمن' : 'Super Admin'}
                sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700 }} />
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.3 }}>
              {isRTL
                ? 'كل سويتش مغلق افتراضياً — تفعيله يُظهر السلوك الجديد، وإغلاقه يعيد النظام كما كان تماماً. لا يمسّ أي بيانات.'
                : 'Every switch is OFF by default — turning it on shows the new behavior, turning it off restores the system exactly as before.'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <Stack spacing={2}>
          {SWITCHES.map(s => (
            <Paper key={s.key} elevation={0} sx={{
              p: 2.5, borderRadius: 3, transition: 'all .2s',
              border: '1px solid', borderColor: values[s.key] ? 'success.main' : 'divider',
              boxShadow: values[s.key] ? '0 6px 20px rgba(46,125,50,0.12)' : '0 2px 12px rgba(0,0,0,0.05)',
              borderInlineStart: values[s.key] ? '5px solid' : '5px solid transparent',
              borderInlineStartColor: values[s.key] ? 'success.main' : 'transparent',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Avatar variant="rounded" sx={{ bgcolor: 'action.hover', width: 42, height: 42 }}>{s.icon}</Avatar>
                  <Box>
                    <Typography fontWeight={700}>{isRTL ? s.titleAr : s.titleEn}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
                      {isRTL ? s.descAr : s.descEn}
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={!!values[s.key]}
                      disabled={saving === s.key}
                      onChange={(e) => toggle(s.key, e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600} color={values[s.key] ? 'success.main' : 'text.secondary'}>
                      {values[s.key] ? (isRTL ? 'مفعّل' : 'ON') : (isRTL ? 'مغلق' : 'OFF')}
                    </Typography>
                  }
                  labelPlacement={isRTL ? 'start' : 'end'}
                />
              </Box>
            </Paper>
          ))}
          <Divider />
          <Alert severity="info" icon={<TuneIcon />}>
            {isRTL
              ? 'المزيد من سويتشات الوسم (وضع الوسم، تصدير الملف) ستُضاف هنا تباعاً أثناء البناء.'
              : 'More XBRL switches (XBRL mode, file export) will be added here as the build progresses.'}
          </Alert>
          <XbrlGeneratorPanel isRTL={isRTL} />
          <XbrlReviewPanel isRTL={isRTL} />
        </Stack>
      )}
    </Box>
  );
};

export default XbrlSettingsTab;
